import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import unifiedApi from '../services/unifiedApi';
import { useFeatureFlags } from './useFeatureFlags.jsx';
import { toast } from 'sonner';
import logger from '@/utils/logger';

export const STOP_COORD_DECIMAL_PLACES = 8;

export function formatStopCoordForDisplay(value) {
  if (value === null || value === undefined || value === '') return '';
  const n =
    typeof value === 'number' && Number.isFinite(value) ? value : parseFloat(String(value).trim().replace(',', '.'));
  if (!Number.isFinite(n)) return '';
  const s = n.toFixed(STOP_COORD_DECIMAL_PLACES);
  const trimmed = s.replace(/\.?0+$/, '');
  return trimmed === '' ? '0' : trimmed;
}

export const useStops = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { hasFeature } = useFeatureFlags();

  const [mapPreviewStop, setMapPreviewStop] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [stopToDelete, setStopToDelete] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFiltersState] = useState({
    search: searchParams.get('q') || '',
    status: searchParams.get('status') || 'active',
  });

  const {
    data,
    isPending: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['stops'],
    queryFn: async () => {
      const [stopsResponse, statsResponse] = await Promise.all([
        unifiedApi.stops.getAll(),
        hasFeature('metrics') ? unifiedApi.stops.getStats() : Promise.resolve(null),
      ]);
      if (!stopsResponse.success) throw new Error(stopsResponse.error || 'Erreur lors du chargement des arrêts');
      return {
        stops: stopsResponse.data || [],
        stats: statsResponse?.data || null,
      };
    },
  });

  const error = queryError?.message ?? null;
  const stops = data?.stops || [];
  const stats = data?.stats || null;
  const pendingStops = useMemo(() => stops.filter((s) => (s.status || '').toLowerCase() === 'pending'), [stops]);
  const loadStops = refetch;
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['stops'] });

  const updateFilters = (updater) => {
    setFiltersState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
      const params = new URLSearchParams(searchParams);
      if (next.search) params.set('q', next.search);
      else params.delete('q');
      if (next.status) params.set('status', next.status);
      else params.delete('status');
      setSearchParams(params, { replace: true });
      return next;
    });
  };

  const filteredStops = stops.filter((stop) => {
    const term = filters.search.toLowerCase();
    const matchesSearch =
      !filters.search ||
      stop.stop_name?.toLowerCase().includes(term) ||
      stop.address?.toLowerCase().includes(term) ||
      stop.description?.toLowerCase().includes(term);
    const matchesStatus = !filters.status || stop.status === filters.status;
    return matchesSearch && matchesStatus;
  });

  const handleAdd = () => navigate('/admin/stops/new');

  const handleEdit = (stop) => navigate(`/admin/stops/${stop.id}/edit`, { state: { stop } });

  const handleToggleStatus = async (stop) => {
    const cur = (stop.status || '').toLowerCase();
    const next = cur === 'active' ? 'inactive' : 'active';
    try {
      await unifiedApi.status.updateStop(stop.id, next);
      toast.success(next === 'active' ? 'Arrêt activé.' : 'Arrêt désactivé.');
      invalidate();
    } catch (err) {
      logger.error('Erreur statut arrêt:', err);
      const msg =
        err.response?.data?.message || err.response?.data?.error || err.message || 'Impossible de modifier le statut';
      toast.error(typeof msg === 'string' ? msg : 'Impossible de modifier le statut');
    }
  };

  const handleApprove = async (stop) => {
    try {
      await unifiedApi.status.updateStop(stop.id, 'active');
      toast.success(`Arrêt "${stop.stop_name}" validé.`);
      invalidate();
    } catch (err) {
      logger.error('Erreur validation arrêt:', err);
      const msg = err.response?.data?.message || err.message || "Impossible de valider l'arrêt";
      toast.error(typeof msg === 'string' ? msg : "Impossible de valider l'arrêt");
    }
  };

  const handleReject = async (stop) => {
    try {
      await unifiedApi.status.updateStop(stop.id, 'inactive');
      toast.success(`Arrêt "${stop.stop_name}" rejeté.`);
      invalidate();
    } catch (err) {
      logger.error('Erreur rejet arrêt:', err);
      const msg = err.response?.data?.message || err.message || "Impossible de rejeter l'arrêt";
      toast.error(typeof msg === 'string' ? msg : "Impossible de rejeter l'arrêt");
    }
  };

  const showPendingOnly = () => updateFilters((prev) => ({ ...prev, status: 'pending' }));

  const handleDelete = (stop) => {
    setStopToDelete(stop);
    setDeleteModalOpen(true);
  };

  const confirmDeleteStop = async () => {
    const stop = stopToDelete;
    setDeleteModalOpen(false);
    setStopToDelete(null);
    if (!stop) return;

    queryClient.setQueryData(['stops'], (old) => ({
      ...old,
      stops: (old?.stops || []).filter((s) => s.id !== stop.id),
    }));

    try {
      await unifiedApi.stops.delete(stop.id);
      toast.success(`Arrêt "${stop.stop_name}" supprimé`);
      invalidate();
    } catch (err) {
      logger.error('Erreur suppression arrêt:', err);
      invalidate();
      const msg = err.response?.data?.message || err.message || "Erreur lors de la suppression de l'arrêt";
      toast.error(typeof msg === 'string' ? msg : "Erreur lors de la suppression de l'arrêt");
    }
  };

  const handleCopyCoordinates = async (stop) => {
    if (stop?.latitude == null || stop?.longitude == null) return;
    const text = `${formatStopCoordForDisplay(stop.latitude)}, ${formatStopCoordForDisplay(stop.longitude)}`;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        const el = document.createElement('textarea');
        el.value = text;
        document.body.appendChild(el);
        el.select();
        document.execCommand('copy');
        document.body.removeChild(el);
      }
      toast.success('Coordonnées copiées.');
    } catch (e) {
      logger.error('Erreur copie coordonnées:', e);
      toast.error('Impossible de copier les coordonnées.');
    }
  };

  return {
    stops,
    filteredStops,
    pendingStops,
    loading,
    error,
    setError: () => {}, // no-op : React Query gère l'état d'erreur
    stats,
    mapPreviewStop,
    setMapPreviewStop,
    deleteModalOpen,
    stopToDelete,
    filters,
    setFilters: updateFilters,
    loadStops,
    handleAdd,
    handleEdit,
    handleToggleStatus,
    handleApprove,
    handleReject,
    showPendingOnly,
    handleDelete,
    confirmDeleteStop,
    setDeleteModalOpen,
    handleCopyCoordinates,
    hasFeature,
  };
};

export default useStops;
